import React, { memo, useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  VStack,
  Text,
  Stack,
  Grid,
  GridItem,
  DrawerOverlay,
  Box,
  Badge,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { VideoPlayer } from '../components';
import { getSubmissionsByHoopId } from '../services/firestore';
import placeholderVideo from '../static/ny-hoops-final.mp4';
import { getRandomIndex } from '../utils';

const HoopsDrawer = memo(({ isOpen, onClose, activeHoop, openUploadModal }) => {
  const [submissions, setSubmissions] = useState([]);
  const [showSubmissionGrid, setShowSubmissionsGrid] = useState(false);

  const handleClose = () => {
    if (showSubmissionGrid) {
      setShowSubmissionsGrid(false);
      return;
    }

    onClose();
  };

  const handleOpenSubmissionForm = () => {
    openUploadModal();
  };

  const handleSeeAllSubmissions = () => {
    setShowSubmissionsGrid(true);
  };

  useEffect(() => {
    const fetchSubmissions = async () => {
      const querySnapshot = await getSubmissionsByHoopId(activeHoop.id);
      const data = querySnapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setSubmissions(data);
    };

    if (activeHoop?.id && submissions.length === 0) fetchSubmissions();
  }, [activeHoop?.id, submissions.length]);

  if (!activeHoop?.id) return null;

  const randomSubmissionIndex = getRandomIndex(submissions.length);

  return (
    <Drawer
      isOpen={isOpen}
      id="hoops-drawer"
      size="lg"
      placement="right"
      scrollBehavior="inside"
      motionPreset="slideInRight"
      returnFocusOnClose={false}
      onClose={handleClose}
      allowPinchZoom
      preserveScrollBarGap
    >
      <DrawerOverlay />
      <DrawerContent backgroundColor="blackAlpha.800">
        <DrawerBody padding={0}>
          {showSubmissionGrid ? (
            <Grid
              maxH="90vh"
              overflowY="scroll"
              templateColumns="repeat(auto-fit, minmax(250px, 1fr))"
              w="full"
              gap={4}
              padding={4}
            >
              {submissions.map((submission, submissionIndex) => (
                <GridItem key={submission.id}>
                  <Box bg="blackAlpha.800">
                    <VideoPlayer
                      src={submission.storageFileUrl}
                      type={submission.fileType}
                      {...(randomSubmissionIndex === submissionIndex && {
                        autoPlay: true,
                        muted: false,
                      })}
                    />
                    <Badge colorScheme="whiteAlpha" fontSize="xs">
                      Submission # {submissionIndex + 1}
                    </Badge>
                  </Box>
                </GridItem>
              ))}
            </Grid>
          ) : (
            <Box maxHeight="50vh">
              {/** TODO: review if badge should go here or below */}
              {/* <Badge
                pos="absolute"
                zIndex={1}
                colorScheme="whiteAlpha"
                fontSize="xs"
                alignSelf="flex-start"
              >
                Submission # {randomSubmissionIndex + 1}
              </Badge> */}
              {submissions.length > 0 ? (
                <VideoPlayer
                  src={submissions[randomSubmissionIndex].storageFileUrl}
                  type={submissions[randomSubmissionIndex].fileType}
                />
              ) : (
                // Fallback placeholder
                <VideoPlayer src={placeholderVideo} type="video/mp4" autoPlay />
              )}

              <VStack align="flex-start" px={[4, 6]} mt={1} spacing={6}>
                <Stack color="white" spacing={1}>
                  {submissions.length > 0 && (
                    <Badge colorScheme="whiteAlpha" fontSize="xs" alignSelf="flex-start">
                      Submission # {randomSubmissionIndex + 1}
                    </Badge>
                  )}
                  <Text fontSize="3xl" fontWeight="bold" noOfLines={2}>
                    {activeHoop.name}
                  </Text>
                  <Text fontSize="xl">{activeHoop.location?.googleFormattedAddress}</Text>
                </Stack>

                <Stack spacing={6}>
                  <Button
                    size="lg"
                    rounded="3xl"
                    colorScheme="knicks"
                    color="black"
                    iconSpacing={4}
                    rightIcon={<FontAwesomeIcon icon={['fas', 'arrow-right']} size="lg" />}
                    onClick={handleOpenSubmissionForm}
                  >
                    Submit yourself getting a bucket
                  </Button>

                  <Button
                    size="lg"
                    rounded="3xl"
                    colorScheme="whiteAlpha"
                    iconSpacing={4}
                    rightIcon={<FontAwesomeIcon icon={['fas', 'arrow-right']} size="lg" />}
                    onClick={handleSeeAllSubmissions}
                    disabled={submissions.length === 0}
                  >
                    See all submissions at this court
                  </Button>
                </Stack>
              </VStack>
            </Box>
          )}

          <Box pos="absolute" bottom="4" left="4">
            <Button
              onClick={handleClose}
              leftIcon={
                <FontAwesomeIcon icon={['fas', showSubmissionGrid ? 'arrow-left' : 'times']} />
              }
              iconSpacing={4}
              colorScheme="whiteAlpha"
              variant="ghost"
              rounded="full"
            >
              {showSubmissionGrid ? 'Back' : 'Close'}
            </Button>
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
});

HoopsDrawer.displayName = 'HoopsDrawer';

export default HoopsDrawer;
